/**
 * SummarisePanel — Claude drift summary for a single service (CORE-912).
 *
 * Lives inside the expanded region of DriftRow. State is lifted to the
 * caller so it survives DriftRow's expand/collapse cycles: DriftRow owns
 * the SummaryState and passes it + a setter here. Re-mounting on expand
 * therefore starts with whatever state was there before (idle on first
 * expand, loaded after a successful fetch).
 *
 * Envelope shape (see @/types/api DriftSummaryResponse): the Lambda
 * caches responses in S3 with 1h TTL; `cache_hit` on the envelope tells
 * us which cost line format to render.
 */

import { useEffect, useState } from 'react';
import { getDriftSummary } from '@/api/client';
import type { DriftSummaryResponse, DriftSummaryAggregateResponse } from '@/types/api';
import type { DriftEnv } from '@/utils/driftConfig';
import { formatCurrency, formatRelativeAge } from '@/utils/format';

/**
 * Lambda cache TTL — mirrors the S3 cache in the drift-summary handler
 * (observability-api/src/lambdas/drift-summary/handler.ts). Within this
 * window, a Refresh click would just re-serve the cached response — a
 * wasted API-Gateway + Lambda invocation with no user benefit. The UI
 * disables the button for the full window and shows a countdown.
 *
 * **Coordination risk:** if the Lambda's TTL rises above this value, a
 * refresh click at elapsed > this_TTL but < server_TTL will get another
 * cache_hit=true response with the same generated_at as before — UI
 * re-computes canRefresh → true (since it uses the same generated_at) →
 * user can loop. Currently mitigated by: (a) both TTLs are 1h; (b) users
 * would notice the "This summary cost $0.00 • 0 in / 0 out" cache marker.
 * A follow-up ticket should transmit cache_ttl_seconds in the response
 * envelope so the UI reads TTL dynamically. Until then, changes to the
 * Lambda TTL MUST land in lockstep with an update to this constant.
 */
const SUMMARY_CACHE_TTL_MS = 60 * 60 * 1000;

export type SummaryState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'loaded'; response: DriftSummaryResponse }
  | { status: 'error'; message: string };

interface SummarisePanelProps {
  readonly from: DriftEnv;
  readonly to: DriftEnv;
  readonly service: string;
  readonly state: SummaryState;
  readonly setState: (state: SummaryState) => void;
  /**
   * Injectable clock — tests pass a fixed date so cached-age rendering is
   * deterministic. Production callers omit and get real wall-clock.
   */
  readonly now?: Date;
  /**
   * Rolling-30-day spend aggregate (fetched by DriftTab, shared across
   * all rows). Powers the cost strip and the button cost hint. Null while
   * loading or after fetch error — both features degrade gracefully by
   * hiding rather than blocking the panel.
   */
  readonly aggregate: DriftSummaryAggregateResponse | null;
}

export function SummarisePanel({
  from,
  to,
  service,
  state,
  setState,
  now,
  aggregate,
}: SummarisePanelProps): JSX.Element {
  // Ticking clock for the Refresh button's disabled→enabled transition.
  // Without this, the button state would stay stale as the cache TTL
  // elapses (React has no reason to re-render on wall-clock changes).
  // Tests inject `now` to keep clock-dependent assertions deterministic
  // — skip the tick in that case. Interval is gated on `loaded` state
  // because that's the only state that consumes effectiveNow (idle /
  // loading / error don't need the tick).
  //
  // Deps use `isControlledNow` (a boolean) rather than the raw `now`
  // prop — a Date reference changes every parent render even when the
  // value is stable, which would thrash the interval identity.
  const isControlledNow = now !== undefined;
  const [tickedNow, setTickedNow] = useState<Date>(() => now ?? new Date());
  useEffect(() => {
    if (isControlledNow) return undefined;
    if (state.status !== 'loaded') return undefined;
    // Snap tickedNow forward on entering loaded state. Otherwise, if the
    // panel sat in idle for minutes before the user clicked Summarise,
    // tickedNow would carry mount-time stale until the first 30s tick.
    setTickedNow(new Date());
    const id = setInterval(() => setTickedNow(new Date()), 30_000);
    return (): void => clearInterval(id);
  }, [isControlledNow, state.status]);
  const effectiveNow = now ?? tickedNow;

  async function fetchSummary(): Promise<void> {
    setState({ status: 'loading' });
    try {
      const response = await getDriftSummary(from, to, service);
      setState({ status: 'loaded', response });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setState({ status: 'error', message });
    }
  }

  const costHint = buttonCostHint(aggregate);

  if (state.status === 'idle') {
    return (
      <div className="summary-panel">
        <CostStrip aggregate={aggregate} />
        <button
          type="button"
          className="summary-fetch-btn"
          onClick={(): void => {
            void fetchSummary();
          }}
        >
          Summarise this drift{costHint !== null && (
            // aria-hidden so the button's accessible name stays "Summarise
            // this drift" regardless of aggregate resolution. Screen readers
            // announce a stable control name; sighted users still see the hint.
            <span className="summary-fetch-btn-hint" aria-hidden="true"> {costHint}</span>
          )}
        </button>
      </div>
    );
  }

  if (state.status === 'loading') {
    return (
      <div className="summary-panel">
        <CostStrip aggregate={aggregate} />
        <div className="summary-loading" role="status" aria-live="polite">
          <span className="summary-spinner" aria-hidden>
            ⟳
          </span>
          <span>Generating summary…</span>
        </div>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="summary-panel">
        <CostStrip aggregate={aggregate} />
        <div className="summary-error" role="alert">
          {/* Nest the two text spans in a flex-column child so a long
              classified message wraps beneath the header instead of
              overflowing the outer flex row (which has align-items:
              center + no wrap). The Retry button stays a sibling —
              `margin-left: auto` pushes it right regardless of the
              text-column width. See .summary-error / .summary-error-text
              in main.css. */}
          <div className="summary-error-text">
            <span className="summary-error-header">Summary unavailable</span>
            {/* CORE-1056c: render the backend's classified message
                (e.g. "GitHub API rate limit reached — retry in an
                hour" or "LLM provider does not recognise the
                configured model — check LLM_MODEL_ID: ..."). The
                generic "Summary unavailable" header above stays as
                the visual anchor; this subtext gives the operator
                the actionable why. Skipped when message is empty
                (defensive — the ApiError message is always populated
                from either the classifier or the fallback, but
                non-ApiError throws could produce empty). */}
            {state.message.length > 0 && (
              <span className="summary-error-detail">{state.message}</span>
            )}
          </div>
          <button
            type="button"
            className="summary-retry-btn"
            onClick={(): void => {
              void fetchSummary();
            }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // Loaded
  const { response } = state;
  return (
    <div className="summary-panel summary-panel-loaded">
      <CostStrip aggregate={aggregate} />
      <div className="summary-card">
        <section className="summary-block" data-testid="summary-block-summary">
          <h5 className="summary-block-heading">Summary</h5>
          <p className="summary-text">{response.summary}</p>
        </section>
        {response.contract_concerns.length > 0 && (
          <section
            className="summary-block summary-block-contract-impact"
            data-testid="summary-block-contract-impact"
          >
            <h5 className="summary-block-heading">Contract impact</h5>
            <ul className="summary-concerns" data-testid="summary-concerns">
              {response.contract_concerns.map((c) => (
                <li key={`${c.file}:${c.concern}`} className="summary-concern">
                  <code className="summary-concern-file">{c.file}</code>
                  <span className={`summary-concern-severity severity-${c.severity}`}>
                    {c.severity}
                  </span>
                  <span className="summary-concern-text">{c.concern}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
        {response.desync_flags.length > 0 && (
          <section
            className="summary-block summary-desync summary-desync-alert"
            data-testid="summary-desync-flags"
          >
            <h5 className="summary-block-heading">Cross-repo attention</h5>
            <ul>
              {response.desync_flags.map((f) => (
                <li key={`${f.backendPr.url}:${f.reason}`} className="summary-desync-flag">
                  <a href={f.backendPr.url} target="_blank" rel="noreferrer">
                    #{f.backendPr.number}: {f.backendPr.title}
                  </a>
                  <p className="summary-desync-reason">{f.reason}</p>
                </li>
              ))}
            </ul>
          </section>
        )}
        <section className="summary-block summary-block-cost" data-testid="summary-block-cost">
          <h5 className="summary-block-heading">Cost</h5>
          <CostLine response={response} now={effectiveNow} />
          <RefreshControl
            generatedAt={response.generated_at}
            now={effectiveNow}
            onRefresh={(): void => {
              void fetchSummary();
            }}
          />
        </section>
      </div>
    </div>
  );
}

function CostLine({
  response,
  now,
}: {
  readonly response: DriftSummaryResponse;
  readonly now: Date;
}): JSX.Element {
  // Both branches return the SAME shape (a single wrapper `<div>` with
  // paragraph children). Previously the cached branch returned a bare
  // `<p>` and the fresh branch returned a fragment of two `<p>`s — a
  // future call site wrapping <CostLine/> in an inline element would
  // produce invalid nested-p HTML from the fresh branch only. Same-shape
  // avoids that class of bug entirely.
  const cost = formatCurrency(response.estimated_cost_usd, 'USD');
  const model = response.pricing_model_id;
  const age = formatRelativeAge(response.generated_at, now);

  if (response.cache_hit) {
    return (
      <div className="summary-cost">
        <p className="summary-cost-line" data-testid="summary-cost-line">
          Cached from {age} • original cost {cost} • {model}
        </p>
      </div>
    );
  }

  const inTok = response.usage.input_tokens.toLocaleString('en-US');
  const outTok = response.usage.output_tokens.toLocaleString('en-US');
  return (
    <div className="summary-cost">
      <p className="summary-cost-line" data-testid="summary-cost-line">
        This summary cost {cost} • {inTok} in / {outTok} out tokens • {model}
      </p>
      <p className="summary-generated-at" data-testid="summary-generated-at">
        Generated {age}
      </p>
    </div>
  );
}

/**
 * Refresh button + "Available in Xm" countdown. Disabled until the Lambda
 * cache TTL has elapsed since generated_at — clicking sooner would just
 * re-serve the cached response, wasting a paid Lambda invocation. The
 * countdown is only shown while disabled; once enabled the button stands
 * alone and clicking triggers a full re-fetch via the same code path as
 * the initial Summarise click.
 */
function RefreshControl({
  generatedAt,
  now,
  onRefresh,
}: {
  readonly generatedAt: string;
  readonly now: Date;
  readonly onRefresh: () => void;
}): JSX.Element {
  const canRefresh = cacheHasExpired(generatedAt, now);
  const countdown = canRefresh ? null : timeUntilCacheExpires(generatedAt, now);
  return (
    <div className="summary-refresh-row">
      <button
        type="button"
        className="summary-refresh-btn"
        disabled={!canRefresh}
        onClick={onRefresh}
        title={canRefresh ? 'Regenerate summary' : `Available in ${countdown}`}
      >
        Refresh
      </button>
      {!canRefresh && countdown !== null && (
        <span
          className="summary-refresh-countdown"
          data-testid="summary-refresh-countdown"
        >
          Available in {countdown}
        </span>
      )}
    </div>
  );
}

function cacheHasExpired(generatedAt: string, now: Date): boolean {
  const generatedMs = new Date(generatedAt).getTime();
  // Malformed timestamp: allow refresh rather than trap the user in a
  // permanently-disabled state. Better one wasted call than a locked UI.
  if (Number.isNaN(generatedMs)) return true;
  return now.getTime() - generatedMs >= SUMMARY_CACHE_TTL_MS;
}

function timeUntilCacheExpires(generatedAt: string, now: Date): string | null {
  const generatedMs = new Date(generatedAt).getTime();
  if (Number.isNaN(generatedMs)) return null;
  // Clamp msRemaining to [0, TTL]. A future generated_at (server clock
  // skew or upstream bug) would otherwise produce an absurd countdown
  // like "Available in 3742m" — cap at the TTL so the worst-case display
  // is still bounded and interpretable.
  const rawRemaining = SUMMARY_CACHE_TTL_MS - (now.getTime() - generatedMs);
  if (rawRemaining <= 0) return null;
  const msRemaining = Math.min(rawRemaining, SUMMARY_CACHE_TTL_MS);
  // Ceil so the last partial minute reads as "1m" not "0m" — no
  // "Available in 0m" flash while the button is still disabled.
  // NOTE: with a 30s tick and 60s ceiling, the "Available in 1m" label
  // can persist for up to 60s before enabling. Acceptable UX drift for
  // an infrequently-used affordance; not worth a sub-minute tick.
  const minutes = Math.ceil(msRemaining / 60_000);
  return `${minutes}m`;
}

/**
 * Rolling-30-day spend rollup strip (CORE-912 PR-C). Rendered above every
 * panel state (idle/loading/error/loaded) so the cost context stays visible
 * regardless of what the user is doing with the summary itself.
 *
 * Null aggregate (loading or fetch error): renders nothing. The strip is
 * informational — hiding it is the right degradation.
 *
 * Zero-count aggregate (day-1 or long-quiet period): shows an explicit
 * "no summaries in the last 30 days" rather than "$0.00 avg" which would
 * misleadingly imply free calls have happened.
 */
function CostStrip({
  aggregate,
}: {
  readonly aggregate: DriftSummaryAggregateResponse | null;
}): JSX.Element | null {
  if (aggregate === null) return null;
  if (aggregate.count === 0) {
    return (
      <p className="summary-cost-strip" data-testid="summary-cost-strip">
        No summaries in the last 30 days.
      </p>
    );
  }
  const total = formatCurrency(aggregate.total_cost_usd, 'USD');
  return (
    <p className="summary-cost-strip" data-testid="summary-cost-strip">
      Last 30 days: {total} • {aggregate.count} summaries
    </p>
  );
}

/**
 * The per-button "~$X.XX" hint appended after "Summarise this drift".
 * Sourced from the aggregate's real 30-day avg — an honest signal of what
 * clicking will typically cost. Returns null (no hint) when aggregate is
 * unavailable or when count is 0 (no observed calls → showing "~$0.00"
 * would read as "free" not "no data").
 */
function buttonCostHint(
  aggregate: DriftSummaryAggregateResponse | null,
): string | null {
  if (aggregate === null || aggregate.count === 0) return null;
  return `(~${formatCurrency(aggregate.avg_cost_usd, 'USD')})`;
}

